/**
 * Department Management Page
 * Requirements: 14.5-14.7 - Department management
 */
import { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { organizationApi } from '../api/organizationApi';
import type { Company, Department } from '../types/organization.types';

export const DepartmentManagementPage = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingDept, setEditingDept] = useState<Department | null>(null);
  const [deptName, setDeptName] = useState('');
  const [deptCompanyId, setDeptCompanyId] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deptToDelete, setDeptToDelete] = useState<Department | null>(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      loadDepartments(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const data = await organizationApi.getCompanies();
      setCompanies(data);
      if (data.length > 0 && !selectedCompanyId) {
        setSelectedCompanyId(data[0].id);
      }
    } catch (err) {
      setError('会社一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async (companyId: string) => {
    try {
      setLoading(true);
      const data = await organizationApi.getDepartmentsByCompany(companyId);
      setDepartments(data);
    } catch (err) {
      setError('部門一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (dept?: Department) => {
    if (dept) {
      setEditingDept(dept);
      setDeptName(dept.name);
      setDeptCompanyId(dept.companyId);
    } else {
      setEditingDept(null);
      setDeptName('');
      setDeptCompanyId(selectedCompanyId);
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editingDept) {
        await organizationApi.updateDepartment(editingDept.id, { name: deptName });
        setSuccess('部門情報を更新しました');
      } else {
        await organizationApi.createDepartment(deptCompanyId, { name: deptName });
        setSuccess('部門を登録しました');
      }
      setOpenDialog(false);
      if (selectedCompanyId) await loadDepartments(selectedCompanyId);
    } catch (err) {
      setError('部門の保存に失敗しました');
    }
  };

  const handleDelete = async () => {
    if (!deptToDelete) return;
    try {
      await organizationApi.deleteDepartment(deptToDelete.id, true);
      setSuccess('部門を削除しました');
      setDeleteDialogOpen(false);
      if (selectedCompanyId) await loadDepartments(selectedCompanyId);
    } catch (err) {
      setError('部門の削除に失敗しました');
    }
  };

  return (
    <Box>
      <Typography variant="h4" mb={3}>部門管理</Typography>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      <Box display="flex" gap={2} mb={3}>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>会社を選択</InputLabel>
          <Select value={selectedCompanyId} onChange={(e) => setSelectedCompanyId(e.target.value)} label="会社を選択">
            {companies.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} disabled={!selectedCompanyId}>
          部門を追加
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>部門名</TableCell>
                    <TableCell>登録日</TableCell>
                    <TableCell align="right">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {departments.length === 0 ? (
                    <TableRow><TableCell colSpan={3} align="center">部門が登録されていません</TableCell></TableRow>
                  ) : (
                    departments.map((dept) => (
                      <TableRow key={dept.id}>
                        <TableCell>{dept.name}</TableCell>
                        <TableCell>{new Date(dept.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell align="right">
                          <IconButton onClick={() => handleOpenDialog(dept)} color="primary" size="small"><EditIcon /></IconButton>
                          <IconButton onClick={() => { setDeptToDelete(dept); setDeleteDialogOpen(true); }} color="error" size="small"><DeleteIcon /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDept ? '部門編集' : '部門追加'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="部門名" fullWidth value={deptName} onChange={(e) => setDeptName(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>キャンセル</Button>
          <Button onClick={handleSave} variant="contained" disabled={!deptName.trim()}>保存</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>部門の削除</DialogTitle>
        <DialogContent>
          <Typography>部門「{deptToDelete?.name}」を削除してもよろしいですか?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleDelete} color="error" variant="contained">削除</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
